import React, { SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import Webcam from 'react-webcam'
import { Box, Button, Image } from '@chakra-ui/react'

interface Props {
  imageUrl: Blob | string | undefined
  uploaded: boolean
  setImageUrl: (value: SetStateAction<Blob | string | undefined>) => void
}

const CameraCapture = (props: Props): JSX.Element => {
  const { setImageUrl, uploaded } = props

  const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState({
      width: window.innerWidth,
      height: window.innerHeight,
    })

    useEffect(() => {
      const handleResize = () => {
        // Remove 15% of size to avoid scrolling:
        setWindowSize({
          width: window.innerWidth - (15 / 100) * window.innerWidth,
          height: window.innerHeight - (15 / 100) * window.innerWidth,
        })
      }

      window.addEventListener('resize', handleResize)
      handleResize()

      return () => window.removeEventListener('resize', handleResize)
    }, [])

    return windowSize
  }

  const size = useWindowSize()

  const isLandscape = size.height <= size.width
  const ratio = isLandscape ? size.width / size.height : size.height / size.width

  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const webcamRef = useRef<Webcam>(null)
  const [currentFacingMode, setCurrentFacingMode] = useState<'user' | { exact: 'environment' }>({
    exact: 'environment',
  })

  const base64toBlob = async (base64Data: string) => {
    const base64Response = await fetch(base64Data)
    const blob = await base64Response.blob()
    setImageUrl(blob)
  }

  const changeVideoConstraints = () => {
    if (currentFacingMode !== 'user') {
      setCurrentFacingMode('user')
    } else {
      setCurrentFacingMode({ exact: 'environment' })
    }
  }

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot()
    if (imageSrc) {
      base64toBlob(imageSrc)
      setImageSrc(imageSrc)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamRef])

  return (
    <>
      <Box>
        {!uploaded && (
          <>
            <Box display="flex" justifyContent="end">
              <Button
                onClick={changeVideoConstraints}
                background="none"
                border="none"
                color="black"
                height={50}
                _focus={{ outline: 'none' }}
                _hover={{ background: 'none' }}
              >
                <img src="/icons/camera-direction.svg" alt="Camera direction" />
              </Button>
            </Box>
            <Box display="flex" justifyContent="center" width={'100%'} height={'75%'}>
              <Webcam
                audio={false}
                height={size.height}
                width={size.width}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={{ facingMode: currentFacingMode, aspectRatio: ratio }}
              />
            </Box>
            <Box display="flex" justifyContent="center">
              <Button
                onClick={capture}
                background="none"
                border="none"
                color="black"
                marginBottom={5}
                height={50}
                _focus={{ outline: 'none' }}
                _hover={{ background: 'none' }}
                marginTop={5}
              >
                <img src="/icons/camera-shutter.svg" alt="Take photo" />
              </Button>
            </Box>
          </>
        )}
        {imageSrc && (
          <>
            <Box display="flex" justifyContent="center" marginBottom={5}>
              <Image height={180} alt="assetPhoto." src={imageSrc || '/images/placeholder.png'} />{' '}
              {!uploaded && (
                <Box justifyContent="center">
                  <Button
                    onClick={() => {
                      setImageUrl(undefined)
                      setImageSrc(null)
                    }}
                    background="none"
                    border="none"
                    color="black"
                    marginBottom={5}
                    _focus={{ outline: 'none' }}
                    _hover={{ background: 'none' }}
                  >
                    <img src="/icons/delete.svg" alt="Delete photo" />
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </>
  )
}

export default CameraCapture
